import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Heart, CheckCircle, MessageCircle, ArrowRight,
  Shield, IndianRupee, Clock, Star, Users
} from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const HealthInsurancePage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Health Insurance Plans in India | Best Medical Insurance | AGO Innovations',
    description:
      'Compare the best health insurance plans in India. AGO Innovations helps you find cashless hospitalisation, family floater, and individual health insurance. Free expert consultation.',
    keywords:
      'health insurance plans India, best health insurance India, cashless health insurance, family health insurance, medical insurance India, health insurance advisor, health cover India',
    canonical: 'https://agoinnovations.in/finance/health-insurance',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      name: 'Health Insurance Advisory — AGO Innovations',
      provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
      description: 'Expert health insurance advisory. Compare individual, family floater, and senior citizen health plans.',
      areaServed: 'IN',
      serviceType: 'Health Insurance Advisory',
    },
  });

  const planTypes = [
    { name: 'Individual Health Plan', desc: 'Cover for a single person. Best for young professionals with specific coverage needs.', badge: 'From ₹3,500/yr', color: 'bg-rose-600' },
    { name: 'Family Floater Plan', desc: 'One policy covers all family members. Cost-effective — the sum insured is shared across the family.', badge: 'Best Value', color: 'bg-pink-600' },
    { name: 'Senior Citizen Plan', desc: 'Designed for people above 60. Higher coverage for age-related illnesses and pre-existing conditions.', badge: 'For Parents', color: 'bg-orange-600' },
    { name: 'Critical Illness Cover', desc: 'Lump sum payout on diagnosis of cancer, heart attack, stroke, and 30+ critical conditions.', badge: 'Important Add-on', color: 'bg-red-700' },
  ];

  const features = [
    { icon: <Heart className="w-5 h-5" />, title: 'Cashless Hospitalisation', desc: 'Get treated at network hospitals without paying upfront. The insurer settles directly.' },
    { icon: <Users className="w-5 h-5" />, title: 'Pre & Post Hospitalisation', desc: '30–60 days before and 60–90 days after hospitalisation expenses covered.' },
    { icon: <IndianRupee className="w-5 h-5" />, title: 'Tax Benefits', desc: 'Premiums up to ₹25,000 deductible under Section 80D (₹50,000 for senior citizens).' },
    { icon: <Clock className="w-5 h-5" />, title: 'No Claim Bonus', desc: 'Sum insured increases by 10–50% for every claim-free year — at no extra cost.' },
    { icon: <Shield className="w-5 h-5" />, title: 'Day Care Procedures', desc: 'Covers 400+ procedures that don\'t require 24-hour hospitalisation.' },
    { icon: <Star className="w-5 h-5" />, title: 'Teleconsultation', desc: 'Many modern plans include unlimited online doctor consultations at no extra charge.' },
  ];

  const faqs = [
    { q: 'How much health insurance cover do I need in India?', a: 'For a single person in a metro city, ₹5–10 lakh is a good starting point. For families, ₹10–15 lakh family floater. Medical costs are rising 15% annually, so opt for a higher sum if possible.' },
    { q: 'What is a family floater health insurance plan?', a: 'A family floater is a single policy that covers all family members under one sum insured. It\'s usually more cost-effective than individual plans for each member.' },
    { q: 'Does health insurance cover pre-existing diseases?', a: 'Most plans have a waiting period of 2–4 years for pre-existing diseases. After the waiting period, all pre-existing conditions are covered.' },
    { q: 'Which health insurance is best in India for 2026?', a: 'Top-rated insurers include Star Health, Niva Bupa (formerly Max Bupa), HDFC ERGO, ICICI Lombard, and Bajaj Allianz. The best plan depends on your age, family size, and city — our advisors help you decide.' },
    { q: 'Can I buy health insurance for my parents?', a: 'Yes. Senior citizen plans are available up to age 80–85. Cover your parents separately with a senior citizen policy for maximum protection.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-gradient-to-br from-rose-900 via-pink-900 to-red-900 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-rose-500/20 rounded-full blur-3xl" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-4xl mx-auto"
        >
          <span className="inline-block bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">
            ❤️ Finance — Health Insurance
          </span>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">
            Health Insurance Plans
            <span className="block text-yellow-400">in India</span>
          </h1>
          <p className="text-rose-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Find the best health insurance for yourself and your family. Cashless hospitalisation, wide network, and tax benefits — compare plans with our free advisory.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/contact')}
              className="bg-yellow-400 hover:bg-yellow-300 text-rose-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              Compare Plans Free <ArrowRight className="w-5 h-5" />
            </motion.button>
            <a
              href="https://wa.me/91XXXXXXXXXX?text=Hi!%20I%20need%20health%20insurance%20advice."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp Us
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-rose-200">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Individual &amp; Family Plans</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Cashless Hospitalisation</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Tax Saving under 80D</span>
          </div>
        </motion.div>
      </section>

      {/* PLAN TYPES */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Types of Health Insurance Plans</h2>
            <p className="text-gray-500 text-lg">Choose the coverage that fits your situation.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {planTypes.map((plan, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-rose-50 rounded-xl flex items-center justify-center text-rose-600">
                    <Heart className="w-6 h-6" />
                  </div>
                  <span className={`text-white text-xs font-bold px-3 py-1 rounded-full ${plan.color}`}>{plan.badge}</span>
                </div>
                <h3 className="font-black text-gray-900 text-lg mb-2">{plan.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{plan.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Key Health Insurance Benefits</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-rose-50 rounded-2xl p-5 border border-rose-100"
              >
                <div className="w-11 h-11 bg-rose-600 rounded-xl flex items-center justify-center text-white mb-4">{f.icon}</div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">{f.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-black text-gray-900 mb-3">Health Insurance FAQs</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-gray-100"
              >
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-rose-800 to-pink-900 text-white text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-black mb-4">Get the Right Health Cover Today</h2>
          <p className="text-rose-200 mb-8">Don't wait for a medical emergency. Let our advisors find the right plan for your budget.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/contact')} className="bg-yellow-400 hover:bg-yellow-300 text-rose-900 font-bold px-8 py-4 rounded-full transition-colors">
              Book Free Consultation
            </button>
            <button onClick={() => navigate('/finance')} className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-8 py-4 rounded-full transition-colors">
              All Finance Services
            </button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default HealthInsurancePage;
